import { format } from 'date-fns';

// Grain operates in Asia/Singapore: every event timing the user picks is a
// Singapore wall-clock value. These helpers anchor to SGT explicitly rather
// than trusting the browser's timezone, so a user editing from any timezone
// still writes (and reads back) the same Singapore instant. Mirrors the
// `stampSingaporeIso` / SGT-parts pattern used by the Closed Hours forms.

const SGT_OFFSET = '+08:00';

// ISO-ordered (`en-CA` → yyyy-MM-dd) parts of a Date's Asia/Singapore
// wall-clock. Pinning `timeZone` means the readout ignores the browser's zone.
const SGT_PARTS_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Singapore',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/**
 * Extract "HH:MM" (24-hour) from an ISO timestamp, reading the wall-clock in
 * Asia/Singapore. Returns '' when the value is unparseable.
 */
export function extractSgtTimeFromIso(iso: string): string {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';
  const parts = SGT_PARTS_FORMATTER.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  // hour12: false occasionally emits "24" for midnight; normalise to "00".
  const hour = get('hour') === '24' ? '00' : get('hour');
  return `${hour}:${get('minute')}`;
}

const SGT_DATE_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Singapore',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export function extractSgtDateFromIso(iso: string): string {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';
  return SGT_DATE_FORMATTER.format(date);
}

/** A Date's local calendar day (the day a picker shows) as YYYY-MM-DD. */
export function toLocalDateKey(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/**
 * Combine a date and an "HH:MM" time into an ISO timestamp, interpreting the
 * time as Asia/Singapore wall-clock. Returns undefined when the time is
 * blank/unparseable. Shared between the EditEvent and CreateEvent mappers for
 * the serving/pickup/setup/teardown timing payloads.
 */
export function combineDateAndTime(date: Date, hhmm: string): string | undefined {
  if (!hhmm) return undefined;
  const [h, m] = hhmm.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return undefined;
  // The calendar day is the day the user selected in the picker (its browser
  // wall-clock); the time is anchored to Singapore so the resulting instant is
  // identical no matter the user's timezone.
  const ymd = toLocalDateKey(date);
  const hm = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  return new Date(`${ymd}T${hm}:00${SGT_OFFSET}`).toISOString();
}
